import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import MaskedValue from '../../components/MaskedValue';
import posthog from 'posthog-js';

const MIN_BUY_AMOUNT = 1;
const MAX_BUY_AMOUNT = 999;
const MIN_SELL_AMOUNT = 0.000001;

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const bitcoinFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 8,
  maximumFractionDigits: 8,
});

const pickNumeric = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return numeric;
    }
  }
  return 0;
};

const toCurrencyString = (value) => currencyFormatter.format(Number(value) || 0);
const toBitcoinString = (value) => bitcoinFormatter.format(Number(value) || 0);

const parseNumericInput = (value, decimals = 2) => {
  if (!value) return NaN;
  const sanitized = String(value).replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
  const numeric = Number(sanitized);
  if (Number.isNaN(numeric)) return NaN;
  return Number(numeric.toFixed(decimals));
};

const formatCurrencyInput = (value) => {
  if (value === null || value === undefined) return '';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '';
  const numeric = Number(digits) / 100;
  return currencyFormatter.format(numeric);
};

const formatBitcoinInput = (value) => {
  if (value === null || value === undefined) return '';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '';
  const numeric = Number(digits) / 1e8;
  return bitcoinFormatter.format(numeric);
};

const Transact = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [balances, setBalances] = useState({ brl: 0, btc: 0 });
  const [loadingBalances, setLoadingBalances] = useState(true);
  const [balancesError, setBalancesError] = useState('');
  const [operation, setOperation] = useState('buy'); // buy | sell
  const [fiatAmount, setFiatAmount] = useState('');
  const [btcAmount, setBtcAmount] = useState('');
  const [formError, setFormError] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [step, setStep] = useState('form'); // form | quote | success
  const [quote, setQuote] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const stepLabel = step === 'form' ? 'Passo 1 de 3' : step === 'quote' ? 'Passo 2 de 3' : 'Passo 3 de 3';

  const fetchBalances = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoadingBalances(true);
      setBalancesError('');
      const response = await api.get(`/dashboard/overview/${user.id}`);
      const data = response.data || {};
      const raw = data.balances?.raw || {};

      const brl = pickNumeric(
        raw.real,
        raw.Real,
        parseNumericInput(data.balances?.formatted?.real, 2)
      );
      const btc = pickNumeric(
        raw.bitcoinAmount,
        raw.BitcoinAmount,
        data.bitcoinBalance?.raw,
        parseNumericInput(data.bitcoinBalance?.formatted, 8)
      );

      setBalances({
        brl: Number.isFinite(brl) ? brl : 0,
        btc: Number.isFinite(btc) ? btc : 0,
      });
    } catch (error) {
      setBalancesError(
        error.response?.data?.message ||
          error.message ||
          'Não foi possível carregar seus saldos. Tente novamente mais tarde.'
      );
    } finally {
      setLoadingBalances(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  const formattedBalances = useMemo(
    () => ({
      brl: toCurrencyString(balances.brl),
      btc: toBitcoinString(balances.btc),
    }),
    [balances]
  );

  const resetFlow = () => {
    setQuote(null);
    setConfirmation(null);
    setGlobalError('');
    setFormError('');
    setStep('form');
  };

  const numericAmount = useMemo(
    () =>
      operation === 'buy'
        ? parseNumericInput(fiatAmount, 2)
        : parseNumericInput(btcAmount, 8),
    [fiatAmount, btcAmount, operation]
  );

  const canSubmitAmount = useMemo(() => {
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      return false;
    }
    if (operation === 'buy') {
      return numericAmount >= MIN_BUY_AMOUNT;
    }
    return numericAmount >= MIN_SELL_AMOUNT;
  }, [numericAmount, operation]);

  const handleOperationChange = (nextOperation) => {
    if (nextOperation === operation) return;
    setOperation(nextOperation);
    setFormError('');
    setGlobalError('');
  };

  const handlePercentageClick = (percentage) => {
    if (operation === 'buy') {
      const value = balances.brl * percentage;
      setFiatAmount(currencyFormatter.format(value));
    } else {
      const value = balances.btc * percentage;
      setBtcAmount(bitcoinFormatter.format(value));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!user?.id) {
      setFormError('Sessão expirada. Faça login novamente.');
      return;
    }

    setFormError('');
    setGlobalError('');

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setFormError('Informe um valor válido.');
      return;
    }

    if (operation === 'buy') {
      if (numericAmount < MIN_BUY_AMOUNT) {
        setFormError(`O valor mínimo para compra é R$ ${MIN_BUY_AMOUNT.toFixed(2)}.`);
        return;
      }
      if (numericAmount > MAX_BUY_AMOUNT) {
        setFormError(`O valor máximo para compra é R$ ${MAX_BUY_AMOUNT.toFixed(2)}.`);
        return;
      }
      if (numericAmount > balances.brl) {
        setFormError('Saldo em Reais insuficiente para esta operação.');
        return;
      }
    } else {
      if (numericAmount < MIN_SELL_AMOUNT) {
        setFormError(`O valor mínimo para venda é ${MIN_SELL_AMOUNT.toFixed(8)} BTC.`);
        return;
      }
      if (numericAmount > balances.btc) {
        setFormError('Saldo em Bitcoin insuficiente para esta operação.');
        return;
      }
    }

    try {
      setIsProcessing(true);
      const response = await api.post('/trade/btc/quote', {
        userId: user.id,
        operation,
        amount: numericAmount,
      });
      setQuote(response.data);
      setStep('quote');
    } catch (error) {
      setGlobalError(
        error.response?.data?.message ||
          error.message ||
          'Não foi possível gerar a cotação. Tente novamente.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirm = async () => {
    if (!quote?.quoteId) {
      setGlobalError('Nenhuma cotação encontrada. Reinicie a operação.');
      return;
    }

    try {
      setIsProcessing(true);
      setGlobalError('');

      const payload = {
        userId: user?.id,
        operation,
        quoteId: quote.quoteId,
      };

      if (operation === 'sell') {
        const btcValue = quote.totalBitcoin?.raw ?? parseNumericInput(btcAmount, 8);
        payload.totalBitcoin = Number.isFinite(btcValue) ? btcValue : undefined;
      }

      const response = await api.post('/trade/btc/confirm', payload);
      const result = response.data;
      setConfirmation(result);
      setStep('success');
      await fetchBalances();

      if (operation === 'buy') {
        posthog.capture('bitcoin_purchase_confirmed', {
          amount_brl: numericAmount,
          btc_received: result?.amountBitcoin?.raw,
        });
      } else {
        posthog.capture('bitcoin_sale_confirmed', {
          amount_btc: numericAmount,
          brl_received: result?.amountFiat?.raw,
        });
      }
    } catch (error) {
      setGlobalError(
        error.response?.data?.message ||
          error.message ||
          'Não foi possível confirmar a operação. Tente novamente.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const renderFormStep = () => (
    <form className="buyingForm mt-6" onSubmit={handleSubmit}>
      <div className="flex items-center mt-6 text-sm font-medium gap-3">
        <button
          type="button"
          className={`flex-1 rounded-full border px-4 py-2 text-center transition-colors ${
            operation === 'buy'
              ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
              : 'border-slate-300 bg-white text-slate-500 hover:bg-slate-50'
          }`}
          onClick={() => handleOperationChange('buy')}
        >
          Comprar
        </button>
        <button
          type="button"
          className={`flex-1 rounded-full border px-4 py-2 text-center transition-colors ${
            operation === 'sell'
              ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
              : 'border-slate-300 bg-white text-slate-500 hover:bg-slate-50'
          }`}
          onClick={() => handleOperationChange('sell')}
        >
          Vender
        </button>
      </div>

      <div className="mt-6 flex items-center justify-between text-xs font-medium text-slate-500">
        <span>Insira o valor</span>
        <div className="flex items-center gap-2">
          {[0.25, 0.5, 0.75, 1].map((percentage) => (
            <button
              key={percentage}
              type="button"
              className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
              onClick={() => handlePercentageClick(percentage)}
            >
              {(percentage * 100).toFixed(0)}%
            </button>
          ))}
        </div>
      </div>

      {operation === 'buy' ? (
        <>
          <div className="mt-3 flex items-center gap-4 rounded-2xl border border-slate-200 bg-[#f3f3f3] px-4 py-3 text-sm font-medium focus-within:border-slate-400">
            <div className="text-slate-400">R$</div>
            <input
              className="w-full bg-transparent outline-none border-0 placeholder:text-slate-400 focus:outline-none focus:ring-0"
              placeholder="1,00 - 999,00"
              value={fiatAmount}
              onChange={(event) => setFiatAmount(formatCurrencyInput(event.target.value))}
            />
          </div>
          <p className="mt-3 text-xs text-slate-400">
            Valor mínimo/máximo: <strong>R$ 1,00 / R$ 999,00</strong>
          </p>
        </>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-4 rounded-2xl border border-slate-200 bg-[#f3f3f3] px-4 py-3 text-sm font-medium focus-within:border-slate-400">
            <div className="text-slate-400 w-16 text-center">BTC</div>
            <input
              className="w-full bg-transparent outline-none border-0 placeholder:text-slate-400 focus:outline-none focus:ring-0"
              placeholder="0,00000000"
              value={btcAmount}
              onChange={(event) => setBtcAmount(formatBitcoinInput(event.target.value))}
            />
          </div>
          <p className="mt-3 text-xs text-slate-400">
            Venda mínima: <strong>0.00000100 BTC</strong>
          </p>
        </>
      )}

      {formError ? (
        <div className="mt-3 text-xs font-semibold text-red-600">{formError}</div>
      ) : null}

      <button
        type="submit"
        className="mt-8 w-full rounded-full bg-tBlue py-3 text-sm font-semibold text-white transition-colors hover:bg-tBlue2 disabled:opacity-60"
        disabled={isProcessing || !canSubmitAmount}
      >
        {isProcessing ? 'Calculando...' : 'Continuar'}
      </button>
    </form>
  );

  const renderQuoteStep = () => (
    <div className="mt-8 space-y-6 text-sm">
      <div className="space-y-4">
        <div className="flex items-center justify-between text-slate-500">
          <span>Tipo de negociação</span>
          <span className="text-slate-900 font-medium">{operation === 'buy' ? 'Compra' : 'Venda'}</span>
        </div>
        <hr className="border-slate-200" />
        <div className="flex items-center justify-between text-slate-500">
          <span>Preço por Bitcoin</span>
          <span className="text-slate-900 font-medium">
            R$ {quote?.estimatedBitcoinPrice?.formatted ?? '0,00'}
          </span>
        </div>
        <hr className="border-slate-200" />
        <div className="flex items-center justify-between text-slate-500">
          <span>{operation === 'buy' ? 'Quantidade' : 'Quantidade (BTC)'}</span>
          <span className="text-slate-900 font-medium">
            {operation === 'buy'
              ? `R$ ${quote?.volume?.formatted ?? '0,00'}`
              : `${quote?.totalBitcoin?.formatted ?? '0,00000000'} BTC`}
          </span>
        </div>
        <hr className="border-slate-200" />
        <div className="flex items-center justify-between text-slate-500">
          <span>Taxa</span>
          <span className="text-slate-900 font-medium">
            {operation === 'buy'
              ? `R$ ${quote?.fee?.formatted ?? '0,00'}`
              : quote?.feeBitcoin?.formatted 
                ? `${quote.feeBitcoin.formatted} BTC`
                : (() => {
                    const feeRaw = quote?.fee?.raw ?? quote?.raw?.fee ?? quote?.raw?.Fee ?? quote?.raw?.transactionFee ?? quote?.raw?.TransactionFee ?? 0;
                    const priceRaw = quote?.estimatedBitcoinPrice?.raw ?? quote?.raw?.estimatedBitcoinPrice ?? quote?.raw?.EstimatedBitcoinPrice ?? quote?.raw?.price ?? quote?.raw?.Price ?? 0;
                    if (feeRaw > 0 && priceRaw > 0) {
                      return `${bitcoinFormatter.format(feeRaw / priceRaw)} BTC`;
                    }
                    return '0,00000000 BTC';
                  })()}
          </span>
        </div>
        <hr className="border-slate-200" />
        <div className="flex items-center justify-between text-slate-500">
          <span>
            {operation === 'buy' ? 'Total aproximado de Bitcoins' : 'Total aproximado de Reais'}
          </span>
          <span className="text-slate-900 font-medium">
            {operation === 'buy'
              ? `${quote?.totalBitcoin?.formatted ?? '0,00000000'} BTC`
              : `R$ ${quote?.volume?.formatted ?? '0,00'}`}
          </span>
        </div>
      </div>

      <div className="pt-2">        
        <button
          type="button"
          className="w-full rounded bg-tBlue py-2 text-sm font-semibold text-white transition-colors hover:bg-tBlue2 disabled:opacity-60"
          onClick={handleConfirm}
          disabled={isProcessing}
        >
          {isProcessing ? 'Confirmando...' : `Confirmar ${operation === 'buy' ? 'Compra' : 'Venda'}`}
        </button>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="mt-8 space-y-6 text-sm">
      <p className="text-slate-500">Dados da sua transação:</p>

      <div className="space-y-4">
        <div className="flex items-center justify-between text-slate-500">
          <span>Tipo de negociação</span>
          <span className={`font-medium ${operation === 'buy' ? 'text-green-600' : 'text-red-600'}`}>{operation === 'buy' ? 'Compra' : 'Venda'}</span>
        </div>
        <hr className="border-slate-200" />
        <div className="flex items-center justify-between text-slate-500">
          <span>Preço por Bitcoin</span>
          <span className="text-slate-900 font-medium">
            R$ {confirmation?.pricePerBitcoin?.formatted ?? '0,00'}
          </span>
        </div>
        <hr className="border-slate-200" />
        <div className="flex items-center justify-between text-slate-500">
          <span>Total em Reais</span>
          <span className="text-slate-900 font-medium">
            R$ {confirmation?.amountFiat?.formatted ?? '0,00'}
          </span>
        </div>
        <hr className="border-slate-200" />
        <div className="flex items-center justify-between text-slate-500">
          <span>Taxa</span>
          <span className="text-slate-900 font-medium">
            {operation === 'buy'
              ? `R$ ${confirmation?.fee?.formatted ?? '0,00'}`
              : confirmation?.feeBitcoin?.formatted 
                ? `${confirmation.feeBitcoin.formatted} BTC`
                : (() => {
                    const feeRaw = confirmation?.fee?.raw ?? confirmation?.raw?.fee ?? confirmation?.raw?.Fee ?? confirmation?.raw?.TransactionFee ?? confirmation?.raw?.transactionFee ?? 0;
                    const priceRaw = confirmation?.pricePerBitcoin?.raw ?? confirmation?.raw?.PriceFinalUserPerBitcoinBrl ?? confirmation?.raw?.priceFinalUserPerBitcoinBrl ?? confirmation?.raw?.finalPrice ?? confirmation?.raw?.price ?? 0;
                    if (feeRaw > 0 && priceRaw > 0) {
                      return `${bitcoinFormatter.format(feeRaw / priceRaw)} BTC`;
                    }
                    return '0,00000000 BTC';
                  })()}
          </span>
        </div>
        <hr className="border-slate-200" />
        <div className="flex items-center justify-between text-slate-500">
          <span>Total em Bitcoins</span>
          <span className="text-slate-900 font-medium">
            {confirmation?.amountBitcoin?.formatted ?? '0,00000000'} BTC
          </span>
        </div>
        {confirmation?.exchangeReference ? (
          <>
            <hr className="border-slate-200" />
            <div className="flex items-center justify-between text-slate-500">
              <span>Referência</span>
              <span className="text-slate-900 font-medium break-all">
                {confirmation.exchangeReference}
              </span>
            </div>
          </>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 pt-2">
        <button
          type="button"
          className="w-full rounded bg-tBlue py-2 text-sm font-semibold text-white transition-colors hover:bg-tBlue2"
          onClick={() => navigate('/dashboard')}
        >
          Voltar ao Dashboard
        </button>
        <button
          type="button"
          className="w-full rounded border border-slate-300 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100"
          onClick={() => {
            resetFlow();
            setFiatAmount('');
            setBtcAmount('');
          }}
        >
          Nova operação
        </button>
      </div>
    </div>
  );

  const contentTitle =
    step === 'form'
      ? 'Converter'
      : step === 'quote'
      ? `Confirmar ${operation === 'buy' ? 'Compra' : 'Venda'}`
      : operation === 'buy' ? 'COMPRA REALIZADA' : 'VENDA REALIZADA';

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="transact" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>

            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="mx-auto max-w-3xl">
            <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm sm:px-10 sm:py-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {step === 'success' && (
                    <i className="bi-check-circle text-green-500 text-2xl"></i>
                  )}
                  <h1 className="text-xl font-semibold text-slate-900">{contentTitle}</h1>
                </div>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">{stepLabel}</span>
              </div>

              {step === 'form' ? (
                <div className="mt-6 text-xs text-slate-500">
                  {loadingBalances ? (
                    <span>Carregando saldos...</span>
                  ) : balancesError ? (
                    <span className="text-red-600">{balancesError}</span>
                  ) : (
                    <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-slate-600">
                      <div className="flex items-center gap-2">
                        <img
                          src="https://static.vecteezy.com/system/resources/previews/011/419/619/original/brazil-flag-file-free-png.png"
                          alt="Bandeira Brasil"
                          className="w-4"
                        />
                        <span>R$ <MaskedValue>{formattedBalances.brl}</MaskedValue></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <img src="/img/others/bitcoin.webp" alt="Bitcoin" className="w-4" />
                        <span>BTC <MaskedValue>{formattedBalances.btc}</MaskedValue></span>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {globalError ? (
                <div className="mt-6 rounded border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
                  {globalError}
                </div>
              ) : null}

              {step === 'form' && renderFormStep()}
              {step === 'quote' && renderQuoteStep()}
              {step === 'success' && renderSuccessStep()}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default Transact;


